import PropTypes from 'prop-types'
import { useContext, useState } from 'react'

import { Menu } from '@material-ui/core'
import MenuItem from '@material-ui/core/MenuItem'

import {
  BotMessageSquare,
  CornerUpLeftIcon,
  CornerUpRightIcon,
  HistoryIcon,
  PencilIcon,
  SmileIcon,
  Trash
} from 'lucide-react'
import { EditMessageContext } from '../../context/EditingMessage/EditingMessageContext'
import { ReplyMessageContext } from '../../context/ReplyingMessage/ReplyingMessageContext'
import { BotReplyContext } from '../../context/BotReply/BotReplyContext'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import ConfirmationModal from '../ConfirmationModal'
import MessageForwardModal from '../MessageForwardModal'
import MessageHistoryModal from '../MessageHistoryModal'
import MessageReactionsMenu from '../MessageReactionsMenu'
import { useStyles } from './style'

const MessageActionsMenu = ({
  message,
  data,
  menuOpen,
  handleClose,
  anchorEl
}) => {
  const { setReplyingMessage } = useContext(ReplyMessageContext)
  const botReplyContext = useContext(BotReplyContext)
  const editingContext = useContext(EditMessageContext)
  const setEditingMessage = editingContext
    ? editingContext.setEditingMessage
    : null

  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [forwardModalOpen, setForwardModalOpen] = useState(false)
  const [messageHistoryOpen, setMessageHistoryOpen] = useState(false)
  const [messageReactionsOpen, setMessageReactionsOpen] = useState(false)

  const [reactionsAnchorEl, setReactionsAnchorEl] = useState(null)

  const classes = useStyles()

  const closeMenu = () => {
    handleClose()
  }

  const handleDeleteMessage = async () => {
    try {
      await api.delete(`/messages/${message.id}`)
    } catch (err) {
      toastError(err)
    }
  }

  const handleReplyMessage = () => {
    setReplyingMessage(message)
    closeMenu()
  }

  const handleReplyBotMessage = e => {
    if (botReplyContext) {
      botReplyContext.openBotReply({ top: e.clientY, left: e.clientX })
    }
    closeMenu()
  }

  const handleOpenConfirmationModal = e => {
    setConfirmationOpen(true)
    closeMenu()
  }

  const handleEditMessage = async () => {
    setEditingMessage(message)
    closeMenu()
  }

  const handleOpenMessageHistoryModal = e => {
    setMessageHistoryOpen(true)
    closeMenu()
  }

  const handleOpenForwardModal = e => {
    setForwardModalOpen(true)
    closeMenu()
  }

  const handleOpenReactionsMenu = event => {
    event.preventDefault()
    event.stopPropagation()
    setReactionsAnchorEl(event.currentTarget) // Define o MenuItem como anchorEl
    setMessageReactionsOpen(true)
    closeMenu() // Agora pode fechar o menu de ações
  }

  const isSticker = data?.message && 'stickerMessage' in data.message

  return (
    <>
      <ConfirmationModal
        title={i18n.t('messageOptionsMenu.confirmationModal.title')}
        open={confirmationOpen}
        onClose={setConfirmationOpen}
        onConfirm={handleDeleteMessage}
      >
        {i18n.t('messageOptionsMenu.confirmationModal.message')}
      </ConfirmationModal>

      <MessageHistoryModal
        open={messageHistoryOpen}
        onClose={setMessageHistoryOpen}
        oldMessages={message.oldMessages}
      />

      <MessageForwardModal
        modalOpen={forwardModalOpen}
        onClose={setForwardModalOpen}
        ticketId={message.ticketId}
        messageId={message.id}
      />

      <MessageReactionsMenu
        message={message}
        menuOpen={messageReactionsOpen}
        handleClose={() => {
          setMessageReactionsOpen(false)
          setReactionsAnchorEl(null) // Limpar o anchorEl ao fechar
        }}
        anchorEl={reactionsAnchorEl}
      />

      <Menu
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        open={menuOpen}
        onClose={closeMenu}
        elevation={0}
        className={classes.menu}
      >
        {message.fromMe && [
          <MenuItem
            key="delete"
            onClick={handleOpenConfirmationModal}
            className={classes.menuItem}
          >
            <Trash />
            {i18n.t('messageOptionsMenu.delete')}
          </MenuItem>,

          !isSticker && (
            <MenuItem
              key="edit"
              onClick={handleEditMessage}
              className={classes.menuItem}
            >
              <PencilIcon />
              {i18n.t('messageOptionsMenu.edit')}
            </MenuItem>
          )
        ]}

        {!isSticker && message.oldMessages?.length > 0 && (
          <MenuItem
            key="history"
            onClick={handleOpenMessageHistoryModal}
            className={classes.menuItem}
          >
            <HistoryIcon />
            {i18n.t('messageOptionsMenu.history')}
          </MenuItem>
        )}

        <MenuItem
          onClick={handleOpenReactionsMenu}
          className={classes.menuItem}
        >
          <SmileIcon />
          Reagir
        </MenuItem>

        <MenuItem onClick={handleReplyMessage} className={classes.menuItem}>
          <CornerUpLeftIcon />
          {i18n.t('messageOptionsMenu.reply')}
        </MenuItem>

        <MenuItem
          key="forward"
          onClick={handleOpenForwardModal}
          className={classes.menuItem}
        >
          <CornerUpRightIcon />
          {i18n.t('messageOptionsMenu.forward')}
        </MenuItem>

        <MenuItem onClick={handleReplyBotMessage} className={classes.menuItem}>
          <BotMessageSquare />
          {i18n.t('messageOptionsMenu.replyBot')}
        </MenuItem>
      </Menu>
    </>
  )
}

MessageActionsMenu.propTypes = {
  message: PropTypes.object.isRequired,
  data: PropTypes.object,
  menuOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  anchorEl: PropTypes.object
}

export default MessageActionsMenu
